'use client';

import { useUserContext } from '@instamenu/context-user';
import {
  Button,
  TextInput,
  Submit,
  ModalNext,
  Text,
  Box,
  Flex,
  SegmentedControls,
} from '@instamenu/ui-system-next';
import toast from 'react-hot-toast';
import { FormEventHandler, useEffect, useState } from 'react';
import { useLocale } from '@instamenu/context-i18n';
import {
  ExportFormatOption,
  buildExportArgs,
  getInitialFormat,
  resolveActiveFormat,
  shouldShowFormatToggle,
} from './export-format';

export type { ExportFormatOption } from './export-format';

type ExportDataButtonProps = {
  onExport: (email: string, format?: string) => Promise<{ success: boolean; error?: string }>;
  /**
   * Optional list of export formats. When supplied (length >= 2), the modal renders
   * a segmented toggle under the email input and passes the selected value as the
   * second argument to `onExport`. When absent, the modal behaves as it did before
   * IME-2641 — no toggle, and `onExport` receives just the email.
   */
  formats?: ExportFormatOption[];
};

export function ExportDataButton({ onExport, formats }: ExportDataButtonProps) {
  const user = useUserContext();
  const { i18n } = useLocale();
  const [email, setEmail] = useState(user.user?.email ?? '');
  const [exportBusy, setExportBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const showFormatToggle = shouldShowFormatToggle(formats);
  const [format, setFormat] = useState<string>(getInitialFormat(formats));
  const activeFormat = resolveActiveFormat(formats, format);
  // Keep `format` in sync when `formats` changes identity (e.g., a parent
  // that lazy-loads its format list, or swaps between different sets). Also
  // clamps the controlled value to a real option if `format` no longer
  // matches — otherwise the segmented toggle would render nothing selected
  // and `buildExportArgs` would pass an empty string on submit.
  useEffect(() => {
    if (!showFormatToggle || !formats?.length) return;
    const stillValid = formats.some((f) => f.value === format);
    if (!stillValid) {
      setFormat(getInitialFormat(formats));
    }
    // `format` is in the dep array only so eslint's exhaustive-deps rule
    // is satisfied — the effect is idempotent: once `stillValid` is true
    // it takes no action, so re-running after `setFormat` fires does not
    // loop.
  }, [formats, showFormatToggle, format]);

  const handleExportData: FormEventHandler = (e) => {
    e.preventDefault();
    if (email.indexOf('@') === -1) return false;
    setExportBusy(true);
    const args = buildExportArgs(email, format, showFormatToggle);
    // Spread guarantees legacy callers see arguments.length === 1 when the
    // toggle is hidden — do NOT change to `onExport(email, format)` without
    // reading the export-format.ts docstring.
    const exportPromise = (
      onExport as (
        ...a: [string] | [string, string]
      ) => Promise<{ success: boolean; error?: string }>
    )(...args);
    exportPromise
      .then((result) => {
        if (result.success) {
          toast.success(i18n.t('exportOrder.dataSent', { email }));
          setOpen(false);
        } else {
          const message = result.error ?? i18n.t('exportOrder.exportError');
          toast.error(message);
        }
      })
      .catch((err) => {
        toast.error(err.message ?? i18n.t('exportOrder.exportError'));
      })
      .finally(() => setExportBusy(false));
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="secondary">
        {i18n.t('export')}
      </Button>
      <ModalNext opened={open} containerStyle={{ maxWidth: 550 }} withCloseButton={false}>
        <Box css={{ padding: 24 }}>
          <Text variant="h3">{i18n.t('export')}</Text>
          <form onSubmit={handleExportData}>
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              {i18n.t('exportOrder.whatEmailExport')}
            </div>
            <TextInput value={email} onChange={(v) => setEmail(v.currentTarget.value)} />
            {showFormatToggle && formats && (
              <Box css={{ marginTop: 20 }}>
                <Text variant="caption1" css={{ marginBottom: 8, fontWeight: 500 }}>
                  {i18n.t('exportOrder.format')}
                </Text>
                <SegmentedControls
                  options={formats.map((f) => ({ label: f.label, value: f.value }))}
                  value={activeFormat?.value ?? format}
                  onChange={setFormat}
                  ariaLabel={i18n.t('exportOrder.format')}
                />
                {activeFormat?.helper && (
                  <Text variant="caption2" css={{ marginTop: 8, opacity: 0.7 }}>
                    {activeFormat.helper}
                  </Text>
                )}
              </Box>
            )}
            <Flex style={{ justifyContent: 'flex-end', gap: 16, marginTop: 16 }}>
              <Button onClick={() => setOpen(false)} variant="secondary">
                {i18n.t('generics.cancel')}
              </Button>
              <Submit onClick={handleExportData} loading={exportBusy}>
                {i18n.t('export')}
              </Submit>
            </Flex>
          </form>
        </Box>
      </ModalNext>
    </>
  );
}
